import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Heart, GitFork, MessageSquare, Compass, Cpu, Zap } from 'lucide-react';

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const load = useCallback(async () => { const res = await api.get('/discover'); setAgents(res.data); }, []);
  useEffect(() => { load(); }, [load]);

  const like = async (a) => {
    const res = await api.post(`/discover/${a.agent_id}/like`);
    setAgents((list) => list.map((x) => x.agent_id === a.agent_id ? { ...x, liked: res.data.liked, likes: x.likes + (res.data.liked ? 1 : -1) } : x));
  };
  const fork = async (a) => { await api.post(`/discover/${a.agent_id}/fork`); toast.success(`Forked "${a.name}" into your agents`); };
  const use = async (a) => { const res = await api.post('/chats', { type: 'direct', agent_ids: [a.agent_id] }); navigate(`/app/chats/${res.data.chat_id}`); };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
        <h1 className="font-display text-xl font-700 flex items-center gap-2"><Compass className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Discover</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Browse community agents. Use them instantly or fork to make them yours.</p>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((a, i) => (
            <motion.div key={a.agent_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 6) * 0.04 }}
              className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 hover:shadow-md transition-shadow flex flex-col" data-testid={`discover-card-${a.agent_id}`}>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl grid place-items-center text-2xl" style={{ background: (a.color || '#0e9488') + '22' }}>{a.avatar}</div>
                <div className="min-w-0"><p className="font-600 truncate">{a.name}</p><p className="text-[11px] text-[hsl(var(--muted-foreground))]">by {a.owner_name}</p></div>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3 line-clamp-2 flex-1">{a.description}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                <span className="flex items-center gap-1 font-mono"><Heart className="h-3.5 w-3.5" />{a.likes}</span>
                <span className="flex items-center gap-1 font-mono"><Zap className="h-3.5 w-3.5" />{a.uses}</span>
                <Badge variant="outline" className="text-[10px] gap-1 font-mono ml-auto"><Cpu className="h-3 w-3" />{a.model}</Badge>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" className="rounded-xl flex-1" style={{ background: 'hsl(var(--primary))' }} onClick={() => use(a)} data-testid={`use-agent-${a.agent_id}`}><MessageSquare className="h-4 w-4 mr-1" /> Use</Button>
                <Button size="sm" variant="secondary" className="rounded-xl border border-[hsl(var(--border))]" onClick={() => fork(a)} data-testid={`fork-agent-${a.agent_id}`}><GitFork className="h-4 w-4" /></Button>
                <Button size="sm" variant="secondary" className={`rounded-xl border border-[hsl(var(--border))] ${a.liked ? 'text-[hsl(var(--destructive))]' : ''}`} onClick={() => like(a)} data-testid={`like-agent-${a.agent_id}`}><Heart className={`h-4 w-4 ${a.liked ? 'fill-current' : ''}`} /></Button>
              </div>
            </motion.div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
